// react
import { useMemo, useState } from 'react';
// react-hook-form
import { Controller, useForm, useWatch } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

// material ui
import {
  Autocomplete,
  Box,
  Card,
  Chip,
  FormHelperText,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// api
import type { GetEnergyTreeNodeDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFFieldLabel, RHFTextField } from '@/components/ui/form';

// components
import {
  getGroupedOptions,
  getTypeColor,
  getTypeIcon,
} from '@/utils/nodeTypeMapper';

// relative
import InitiativeInclusionNotice from '../components/tree/InitiativeInclusionNotice';
import {
  isResidualNode,
  mapUnitsToDataStreams,
} from '../utils/energyTreeFunctions';
import { toEditNodeFormValues } from './editNodeForm.mappers';
import type { EditNodeFormValues } from './editNodeForm.schema';
import { createEditNodeFormSchema } from './editNodeForm.schema';

// libraries
import { zodResolver } from '@hookform/resolvers/zod';

interface Unit {
  UnitId: number;
  Name: string;
  Tags?: Array<{ TagId: number; Text: string }>;
  UnitSensors?: Array<{ Sensor?: { SensorId: number } }>;
  usedInNode?: string;
  notSupportedReason?: string;
}

interface Inclusion {
  viaNode?: unknown;
}

type EditNodeFormProps = {
  energyTreeNode: GetEnergyTreeNodeDto;
  units: Unit[];
  virtualInitiativeInclusions: Inclusion[];
  onSubmit: (values: EditNodeFormValues) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
};

export default function EditNodeForm({
  energyTreeNode,
  units,
  virtualInitiativeInclusions,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: EditNodeFormProps) {
  const { t } = useTranslation(['energyxplorer-tree-tab', 'validation']);

  const [searchText, setSearchText] = useState('');

  const schema = createEditNodeFormSchema(t);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<EditNodeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toEditNodeFormValues(energyTreeNode),
    mode: 'onTouched',
  });

  const groupedOptions = getGroupedOptions(t);
  const specialNodeType = useWatch({
    control,
    name: 'options.specialNodeType',
  });
  const dataStreams = useWatch({ control, name: 'dataStreams' });

  const selectedUnits = useMemo(() => {
    if (!units) return [];
    const byId = new Map(units.map((u) => [u.UnitId, u]));
    return (dataStreams ?? []).map((ds) => byId.get(ds.unitId)).filter(Boolean);
  }, [units, dataStreams]);

  const onSubmitForm = handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <form onSubmit={onSubmitForm} noValidate>
      <Stack spacing={2}>
        <Stack spacing={2}>
          <RHFTextField
            data-testid="edit-node-name-input"
            name="name"
            fullWidth
            control={control}
            label={t('edit_node_dialog.name_label', {
              ns: 'energyxplorer-tree-tab',
            })}
            helperText={t('edit_node_dialog.name_helper', {
              ns: 'energyxplorer-tree-tab',
            })}
            required
          />

          <Controller
            name="type"
            control={control}
            render={({ field, fieldState }) => (
              <Box>
                <RHFFieldLabel required>
                  {t('edit_node_dialog.type_label', {
                    ns: 'energyxplorer-tree-tab',
                  })}
                </RHFFieldLabel>
                <TextField
                  select
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    t('edit_node_dialog.type_helper', {
                      ns: 'energyxplorer-tree-tab',
                    })
                  }
                  fullWidth
                >
                  <MenuItem value="" disabled>
                    <Typography color="text.secondary">
                      {t('nodes.edit_dialog.select_type', {
                        ns: 'energyxplorer',
                      })}
                    </Typography>
                  </MenuItem>

                  {Object.entries(groupedOptions).map(
                    ([groupKey, groupData]) => {
                      if (!groupData.options.length) return null;

                      return [
                        <ListSubheader
                          key={`header-${groupKey}`}
                          sx={{ bgcolor: 'background.paper' }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              py: 1,
                            }}
                          >
                            <CategoryRoundedIcon fontSize="small" />
                            <Typography fontWeight="bold">
                              {groupData.label}
                            </Typography>
                          </Box>
                        </ListSubheader>,
                        ...groupData.options.map(
                          ({
                            value,
                            label,
                          }: {
                            value: string;
                            label: string;
                          }) => (
                            <MenuItem key={value} value={value}>
                              <Box display="flex" width="100%">
                                <Box
                                  sx={{
                                    backgroundColor: getTypeColor(value),
                                    color: 'white',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    mr: 1,
                                    height: 28,
                                    width: 28,
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                  }}
                                >
                                  <i
                                    className={`fa-solid ${getTypeIcon(value)}`}
                                  />
                                </Box>
                                <Typography>{label}</Typography>
                              </Box>
                            </MenuItem>
                          ),
                        ),
                      ];
                    },
                  )}
                </TextField>
              </Box>
            )}
          />

          {units && !isResidualNode(energyTreeNode) && (
            <Controller
              name="dataStreams"
              control={control}
              render={({ field }) => (
                <Box>
                  <RHFFieldLabel>
                    {t('edit_node_dialog.data_source_label', {
                      ns: 'energyxplorer-tree-tab',
                    })}
                  </RHFFieldLabel>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    options={units || []}
                    getOptionLabel={(option) =>
                      `${option.UnitId} - ${option.Name}`
                    }
                    value={selectedUnits as Unit[]}
                    isOptionEqualToValue={(option, value) =>
                      option.UnitId === value.UnitId
                    }
                    inputValue={searchText}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason !== 'reset') {
                        setSearchText(newInputValue);
                      }
                    }}
                    getOptionDisabled={(option) =>
                      !!option.usedInNode || !!option.notSupportedReason
                    }
                    onClose={() => setSearchText('')}
                    onChange={(e, newValue) => {
                      field.onChange(mapUnitsToDataStreams(newValue));
                    }}
                    filterSelectedOptions
                    filterOptions={(options, { inputValue }) => {
                      const search = inputValue.trim().toLowerCase();
                      return options.filter((unit) => {
                        if (!unit) return false;
                        const nameMatch =
                          unit.Name?.toLowerCase().includes(search);
                        const tagMatch = unit.Tags?.some((tag) =>
                          tag.Text?.toLowerCase().includes(search),
                        );
                        const sensorMatch = unit.UnitSensors?.some((s) =>
                          String(s.Sensor?.SensorId).includes(search),
                        );
                        const unitIdMatch = String(unit.UnitId).includes(
                          search,
                        );
                        return (
                          nameMatch || tagMatch || sensorMatch || unitIdMatch
                        );
                      });
                    }}
                    renderOption={(props, option) => {
                      if (!option) return null;
                      const { key, ...rest } = props;
                      return (
                        <li key={key} {...rest}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}
                          >
                            <Typography>
                              ({option.UnitId}) {option.Name}
                            </Typography>
                            {option?.Tags?.length > 0 && (
                              <Box>
                                {option.Tags.map((tag) => (
                                  <Chip
                                    key={tag.TagId}
                                    label={tag.Text}
                                    size="small"
                                    sx={{ mr: 0.5 }}
                                  />
                                ))}
                              </Box>
                            )}
                            {option.usedInNode && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'gray' }}
                              >
                                {t('nodes.edit_dialog.applied_for', {
                                  ns: 'energyxplorer',
                                })}{' '}
                                {option.usedInNode}
                              </Typography>
                            )}
                            {option.notSupportedReason && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'red' }}
                              >
                                {option.notSupportedReason}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-testid="edit-node-data-source-input"
                        placeholder={t(
                          'edit_node_dialog.data_source_placeholder',
                          {
                            ns: 'energyxplorer-tree-tab',
                          },
                        )}
                        helperText={t('edit_node_dialog.data_source_helper', {
                          ns: 'energyxplorer-tree-tab',
                        })}
                      />
                    )}
                    renderValue={(value, getItemProps) =>
                      value.map((option, index) => {
                        if (!option) return null;
                        const { key, ...tagProps } = getItemProps({ index });
                        return (
                          <Chip
                            sx={{ borderRadius: 1 }}
                            size="small"
                            key={key}
                            label={option.Name}
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Box>
              )}
            />
          )}
        </Stack>

        {isResidualNode(energyTreeNode) && (
          <Card sx={{ p: 2, mt: 2, mb: 2, bgcolor: 'background.default' }}>
            <FormHelperText>
              {t('nodes.edit_dialog.residual_message', {
                ns: 'energyxplorer',
              })}
            </FormHelperText>
            <Controller
              name="options.specialNodeType"
              control={control}
              render={({ field, fieldState }) => (
                <Box sx={{ my: 2 }}>
                  <TextField
                    data-testid="edit-node-residual-type-select"
                    select
                    label="Rolle"
                    value={field.value || ''}
                    onChange={(e) => {
                      const next = e.target.value;
                      field.onChange(next);
                      // Clear conditional fields when switching type
                      if (next !== 'FlatResidual') {
                        setValue('options.consumption', null);
                      }
                      if (next !== 'PercentageResidual') {
                        setValue('options.percentage', null);
                      }
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    variant="filled"
                    slotProps={{
                      input: {
                        disableUnderline: true,
                        sx: { borderRadius: 1 },
                      }
                    }}
                  >
                    <MenuItem value="RestResidual">
                      {t('edit_node_dialog.residual_rest_value', {
                        ns: 'energyxplorer-tree-tab',
                      })}
                    </MenuItem>
                    <MenuItem value="FlatResidual">
                      {t('edit_node_dialog.residual_fixed_value', {
                        ns: 'energyxplorer-tree-tab',
                      })}{' '}
                      {t('nodes.edit_dialog.kw', { ns: 'energyxplorer' })}
                    </MenuItem>
                    <MenuItem value="PercentageResidual">
                      {t('edit_node_dialog.residual_percentage_value', {
                        ns: 'energyxplorer-tree-tab',
                      })}{' '}
                      {t('nodes.edit_dialog.percent', {
                        ns: 'energyxplorer',
                      })}
                    </MenuItem>
                  </TextField>
                </Box>
              )}
            />

            {specialNodeType === 'FlatResidual' && (
              <Controller
                name="options.consumption"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    data-testid="edit-node-consumption-input"
                    label={t('edit_node_dialog.residual_fixed_value_helper', {
                      ns: 'energyxplorer-tree-tab',
                    })}
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value === '' ? null : Number(e.target.value),
                      );
                    }}
                    variant="filled"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      t('edit_node_dialog.residual_fixed_value_helper', {
                        ns: 'energyxplorer-tree-tab',
                      })
                    }
                    fullWidth
                    slotProps={{
                      input: {
                        disableUnderline: true,
                        sx: { borderRadius: 1 },
                      },

                      htmlInput: { min: 0, step: 'any' }
                    }} />
                )}
              />
            )}

            {specialNodeType === 'PercentageResidual' && (
              <Controller
                name="options.percentage"
                control={control}
                render={({ field, fieldState }) => (
                  <Box sx={{ mt: 2 }}>
                    <Typography gutterBottom>
                      {t('edit_node_dialog.residual_percentage_value', {
                        ns: 'energyxplorer-tree-tab',
                      })}{' '}
                      {t('nodes.edit_dialog.percent', {
                        ns: 'energyxplorer',
                      })}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <Slider
                        value={(field.value || 0) * 100}
                        onChange={(_, newVal) =>
                          field.onChange((newVal as number) / 100)
                        }
                        min={0.1}
                        max={100}
                        step={0.1}
                      />
                      <TextField
                        data-testid="edit-node-percentage-input"
                        type="number"
                        hiddenLabel
                        value={((field.value ?? 0) * 100).toFixed(1)}
                        onChange={(e) => {
                          const pct = parseFloat(e.target.value);
                          const clamped = isNaN(pct)
                            ? 0
                            : Math.min(Math.max(pct, 0), 100);
                          field.onChange(clamped / 100);
                        }}
                        sx={{ width: 100 }}
                        variant="standard"
                        slotProps={{
                          input: {
                            disableUnderline: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <PercentRoundedIcon />
                              </InputAdornment>
                            ),
                          }
                        }}
                      />
                    </Box>
                    {fieldState.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ ml: 2 }}
                      >
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            )}
          </Card>
        )}

        <InitiativeInclusionNotice
          virtualInitiativeInclusions={virtualInitiativeInclusions}
          mode="edit"
        />

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton onClick={onCancel} disabled={isSubmitting}>
            {t('edit_node_dialog.cancel', { ns: 'energyxplorer-tree-tab' })}
          </CancelButton>
          <SubmitButton disabled={!isValid || isSubmitting}>
            {t('edit_node_dialog.confirm', {
              ns: 'energyxplorer-tree-tab',
            })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
